import { useAnalytics } from 'app/context/Analytics';
import React, { useCallback } from 'react';
import useLinks from 'app/modules/tailor/hooks/useLinks';
import { TopicSource, TopicType } from 'app/copied/constants/common';

import { PosthogEventName } from '../../../copied/constants/PosthogEventName';
import {
  AnalyticsTagType,
  ChatBotFormAnswer,
  ChatFormItem,
  ChatFormItemType,
  ChatItem,
  ChatItemForm,
  ChatItemKBEntry,
  ChatItemMessage,
  ChatItemType,
} from '../../../services/chatBot';

export default function useVirtualAssistantTracking() {
  const { captureEvent, captureEventWithActiveScreen, captureAnalyticsTagEvent } = useAnalytics();
  const { shouldShowInterstitial } = useLinks();

  const captureAnswerSubmitted = useCallback(
    (formItem: ChatFormItem, answer: ChatBotFormAnswer, isConvAiActive: boolean, isConvAiChatItem: boolean) => {
      captureAnalyticsTagEvent(formItem.analytics_tags || [], {
        eventValue: answer.value || '',
        eventType: AnalyticsTagType.CHANGE,
      });

      switch (formItem.item_type) {
        case ChatFormItemType.SINGE_CHOICE:
          captureEventWithActiveScreen(PosthogEventName.VirtualChatOptionclick, {
            optionLabel: answer.value,
            isConvAiActive,
            isConvAiChatItem,
          });
          break;
        case ChatFormItemType.INPUT:
          captureEventWithActiveScreen(PosthogEventName.VirtualChaUserDataSubmitted, {
            inputType: formItem.value_role,
            isConvAiActive,
            isConvAiChatItem,
          });
          break;
      }
    },
    [captureAnalyticsTagEvent, captureEventWithActiveScreen]
  );

  const captureAnswerSubmissionErrors = useCallback(
    (formItem: ChatFormItem) => {
      captureAnalyticsTagEvent(formItem.analytics_tags || [], {
        eventValue: formItem.value ?? '',
        eventType: AnalyticsTagType.ERROR,
      });
    },
    [captureAnalyticsTagEvent]
  );

  const captureFormDismissed = useCallback(
    (formItem: ChatFormItem, isConvAiActive: boolean, isConvAiChatItem: boolean) => {
      captureAnalyticsTagEvent(formItem.analytics_tags || [], {
        eventValue: '',
        eventType: AnalyticsTagType.DISMISS,
      });

      captureEventWithActiveScreen(PosthogEventName.VirtualChatUserDataSkipped, {
        inputType: formItem.value_role,
        isConvAiActive,
        isConvAiChatItem,
      });
    },
    [captureAnalyticsTagEvent, captureEventWithActiveScreen]
  );

  const captureLinkClicked = useCallback(
    (e: React.MouseEvent, isConvAiActive: boolean, isConvAiChatItem: boolean) => {
      const link = (e.target as Element).closest('a');
      if (!link) return;
      const href = link.href;
      captureEventWithActiveScreen(PosthogEventName.VirtualChatLinkClicked, {
        linkUrl: href,
        inMessage: true,
        linkType: 'url',
        isConvAiActive,
        isConvAiChatItem,
        toInterstitial: shouldShowInterstitial(href),
      });
    },
    [captureEventWithActiveScreen, shouldShowInterstitial]
  );

  const captureChatOptionPresented = useCallback(
    (options: string[], isConvAiActive: boolean, isConvAiChatItem: boolean, query?: string) => {
      captureEventWithActiveScreen(PosthogEventName.VirtualChatOptionPresented, {
        options,
        isConvAiActive,
        isConvAiChatItem,
        query,
      });
    },
    [captureEventWithActiveScreen]
  );

  const captureMessageSent = useCallback(
    (chatItem: ChatItem, isConvAiActive: boolean, isConvAiChatItem: boolean, additionalProps: object = {}) => {
      const isMessage = chatItem.type === ChatItemType.MESSAGE;
      const isForm = chatItem.type === ChatItemType.FORM;
      const isContact =
        isForm &&
        (chatItem as ChatItemForm).items.some(
          (item) => item.ref_id === 'contact-info' || item.ref_id.startsWith('collect-info')
        );
      captureEventWithActiveScreen(PosthogEventName.VirtualChatVAMessageSent, {
        ...(isMessage && { messageText: (chatItem as ChatItemMessage).message }),
        ...additionalProps,
        isConvAiActive,
        isConvAiChatItem,
        query: chatItem.queryTyped,
      });

      if (isContact) {
        captureEventWithActiveScreen(PosthogEventName.LiveChatContactRequested);
      }
    },
    [captureEventWithActiveScreen]
  );

  const captureFormPresented = useCallback(
    (chatItem: ChatItemForm, isConvAiActive: boolean, isConvAiChatItem: boolean, additionalProps: object = {}) => {
      captureMessageSent(chatItem, isConvAiActive, isConvAiChatItem, additionalProps);

      chatItem.items.forEach((formItem) => {
        captureAnalyticsTagEvent(formItem.analytics_tags || [], {
          eventValue: '',
          eventType: AnalyticsTagType.VIEW,
        });
      });
    },
    [captureMessageSent, captureAnalyticsTagEvent]
  );

  const capturePageview = useCallback(
    (autoOpenDelay: number) => {
      captureEventWithActiveScreen(PosthogEventName.VirtualChatPageview, { autoopenDelay: autoOpenDelay });
    },
    [captureEventWithActiveScreen]
  );

  const captureQuerySubmitted = useCallback(
    ({ query, isConvAiActive }: { query: string; isConvAiActive: boolean }) => {
      captureEventWithActiveScreen(PosthogEventName.AIChatQuerySubmitted, {
        query,
        isConvAiActive,
      });
    },
    [captureEventWithActiveScreen]
  );

  const captureLocationResultsDisplayed = useCallback(
    (queryZip: string, locations: string[]) => {
      captureEvent(PosthogEventName.VirtualChatLocationFinderResult, { queryZip, queryResult: locations });
    },
    [captureEvent]
  );

  const captureLocationResultClick = useCallback(
    (locationId: string, linkUrl: string) => {
      captureEvent(PosthogEventName.VirtualChatLocationFinderResultClick, { id: locationId, linkUrl });
    },
    [captureEvent]
  );

  const captureKBEntryClicked = useCallback(
    ({
      queryTyped,
      resultIndex,
      topicSource,
      topicType,
      topicTitle,
      kbEntryType,
      isConvAiChatItem,
      linkUrl,
    }: {
      queryTyped: string;
      resultIndex: number;
      topicSource?: TopicSource;
      topicType?: TopicType;
      topicTitle?: string;
      kbEntryType: PosthogEventName.RelatedResourcesClicked | PosthogEventName.TopicSourceClicked;
      isConvAiChatItem: boolean;
      linkUrl?: string;
    }) =>
      captureEventWithActiveScreen(kbEntryType, {
        queryTyped,
        resultIndex,
        topicSource,
        topicType,
        topicTitle,
        isConvAiChatItem,
        linkUrl,
      }),
    [captureEventWithActiveScreen]
  );

  const captureKBEntryPresented = useCallback(
    (chatItem: ChatItemKBEntry, isConvAiActive: boolean, isConvAiChatItem: boolean) => {
      captureEventWithActiveScreen(PosthogEventName.FAQLinkSent, {
        queryTyped: chatItem.queryTyped || '',
        resultIndex: chatItem.resultIndex || 0,
        viewType: chatItem.view_type,
        resultType: chatItem.result_type,
        title: chatItem.title,
        description: chatItem.description,
        isConvAiActive,
        isConvAiChatItem,
      });
    },
    [captureEventWithActiveScreen]
  );

  return {
    captureAnswerSubmitted,
    captureAnswerSubmissionErrors,
    captureFormDismissed,
    captureFormPresented,
    captureLinkClicked,
    captureLocationResultClick,
    captureLocationResultsDisplayed,
    captureChatOptionPresented,
    captureMessageSent,
    capturePageview,
    captureQuerySubmitted,
    captureKBEntryClicked,
    captureKBEntryPresented,
  };
}
